import * as React from 'react';
import { toast } from 'react-toastify';
import { Action } from 'redux';

import { ToastComponent, ToastType } from '@common/components';
import { ToastOptions } from '@common/components/toast/toast';
import { SwoAuthoReportSummarySearchViewModel } from '@models/AuthReportSummary';
import { StatusCountViewModel, TradeOptionsViewModel, AuthorizationCountViewModel } from '@models/ServiceWorkOrder';
import { PageViewModel } from '@models/common.models';
import { Dispatch } from 'redux';
import { AppState } from '../../..';
import { requestAction } from '@common/components/common.actions';
import { authorizationService } from '@services/AuthorizationService';
import { ProfileViewModel } from '@models/Auth';

export enum AuthorizationSearchActionType {
  GetAuthoStatusCountsStart = 'GetAuthoStatusCountsStart',
  GetAuthoStatusCountsSuccess = 'GetAuthoStatusCountsSuccess',
  GetAuthoStatusCountsFailure = 'GetAuthoStatusCountsFailure',
  GetTradesFailure = 'GetTradesFailure',
  GetTradesStart = 'GetTradesStart',
  GetTradesSuccess = 'GetTradesSuccess',
  SearchAuthoListStart = 'SearchAuthoListStart',
  SearchAuthoListSuccess = 'SearchAuthoListSuccess',
  SearchAuthoListFailure = 'SearchAuthoListFailure',
  SetAuthorizationCount = 'SetAuthorizationCount',

  ChangeAuthoOwnershipStart = 'ChangeAuthoOwnershipStart',
  ChangeAuthoOwnershipSuccess = 'ChangeAuthoOwnershipSuccess',
  ChangeAuthoOwnershipFailure = 'ChangeAuthoOwnershipFailure'
}

interface AuthorizationSearchAction extends Action {
  readonly type: AuthorizationSearchActionType;
}

export interface GetAuthoStatusCountsStartAction extends AuthorizationSearchAction {}
export interface GetAuthoStatusCountsSuccessAction extends AuthorizationSearchAction {
  readonly result: StatusCountViewModel;
}
export interface GetAuthoStatusCountsFailureAction extends AuthorizationSearchAction {}

export interface GetTradesFailureAction extends AuthorizationSearchAction {}
export interface GetTradesStartAction extends AuthorizationSearchAction {}
export interface GetTradesSuccessAction extends AuthorizationSearchAction {
  readonly results: TradeOptionsViewModel[];
}

export interface SearchAuthoListStartAction extends AuthorizationSearchAction {
  readonly query: string | null;
  readonly trade: string | null;
  readonly priority: string | null;
  readonly stateAbbr: string | null;
  readonly vendorGroup: string | null;
  readonly dispatchType: string | null;
}
export interface SearchAuthoListSuccessAction extends AuthorizationSearchAction {
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly orderBy: string;
  readonly results: PageViewModel<SwoAuthoReportSummarySearchViewModel>;
}
export interface SearchAuthoListFailureAction extends AuthorizationSearchAction {}
export interface SetAuthorizationCountAction extends AuthorizationSearchAction {
  readonly result: AuthorizationCountViewModel;
}

export interface ChangeAuthoOwnershipStartAction extends AuthorizationSearchAction {}
export interface ChangeAuthoOwnershipSuccessAction extends AuthorizationSearchAction {
  readonly user?: ProfileViewModel;
  readonly swoId: number;
  readonly release?: boolean;
}
export interface ChangeAuthoOwnershipFailureAction extends AuthorizationSearchAction {
  readonly error: string;
}

/***** Get Autho Stats Counts *****/
export function getAuthoStatusCountsStart(): GetAuthoStatusCountsStartAction {
  return {
    type: AuthorizationSearchActionType.GetAuthoStatusCountsStart
  };
}

export function getAuthoStatusCountsSuccess(result: StatusCountViewModel): GetAuthoStatusCountsSuccessAction {
  return {
    type: AuthorizationSearchActionType.GetAuthoStatusCountsSuccess,
    result: result
  };
}

export function getAuthoStatusCountsFailure(error: string): GetAuthoStatusCountsFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={error.toString()} />, ToastOptions.Error);
  return {
    type: AuthorizationSearchActionType.GetAuthoStatusCountsFailure
  };
}

/***** Get Trades *****/
export function getTradesStart(): GetTradesStartAction {
  return {
    type: AuthorizationSearchActionType.GetTradesStart
  };
}

export function getTradesSuccess(results: TradeOptionsViewModel[]): GetTradesSuccessAction {
  return {
    type: AuthorizationSearchActionType.GetTradesSuccess,
    results: results
  };
}

export function getTradesFailure(error: string): GetTradesFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={error.toString()} />, ToastOptions.Error);
  return {
    type: AuthorizationSearchActionType.GetTradesFailure
  };
}

/***** Search Auth List *****/
export function searchAuthoListStart(
  query: string | null,
  trade: string | null,
  priority: string | null,
  stateAbbr: string | null,
  vendorGroup: string | null,
  dispatchType: string | null
): SearchAuthoListStartAction {
  return {
    type: AuthorizationSearchActionType.SearchAuthoListStart,
    query: query,
    trade: trade,
    priority: priority,
    stateAbbr: stateAbbr,
    vendorGroup: vendorGroup,
    dispatchType: dispatchType
  };
}

export function searchAuthoListSuccess(
  pageNumber: number,
  pageSize: number,
  orderBy: string,
  results: PageViewModel<SwoAuthoReportSummarySearchViewModel>
): SearchAuthoListSuccessAction {
  return {
    type: AuthorizationSearchActionType.SearchAuthoListSuccess,
    pageNumber: pageNumber,
    pageSize: pageSize,
    orderBy: orderBy,
    results: results
  };
}

export function searchAuthoListFailure(error: string): SearchAuthoListFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={error.toString()} />, ToastOptions.Error);
  return {
    type: AuthorizationSearchActionType.SearchAuthoListFailure
  };
}

/***** Redispatch SWO *****/
export function setAuthorizationCount(authorizationCount: AuthorizationCountViewModel): SetAuthorizationCountAction {
  return {
    type: AuthorizationSearchActionType.SetAuthorizationCount,
    result: authorizationCount
  };
}
export function getAuthorizationCount() {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(requestAction(() => authorizationService.getAuthorizationCount(), setAuthorizationCount));
  };
}

/*** Change Ownership ***/

export function changeAuthoOwnership(swoId: number, release: boolean) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    dispatch(changeAuthoOwnershipStart());
    try {
      if (release === true) {
        await authorizationService.release(swoId);
        dispatch(changeAuthoOwnershipSuccess(swoId, undefined, release));
      } else {
        await authorizationService.own(swoId);
        const user: ProfileViewModel = getState().auth.currentProfile!;
        dispatch(changeAuthoOwnershipSuccess(swoId, user, release));
      }
    } catch (e) {
      dispatch(changeAuthoOwnershipFailure(e.toString()));
    }
  };
}

export function changeAuthoOwnershipStart(): ChangeAuthoOwnershipStartAction {
  return {
    type: AuthorizationSearchActionType.ChangeAuthoOwnershipStart
  };
}

export function changeAuthoOwnershipSuccess(
  swoId: number,
  user?: ProfileViewModel,
  release?: boolean
): ChangeAuthoOwnershipSuccessAction {
  return {
    type: AuthorizationSearchActionType.ChangeAuthoOwnershipSuccess,
    user: user,
    swoId: swoId,
    release: release
  };
}

export function changeAuthoOwnershipFailure(error: string): ChangeAuthoOwnershipFailureAction {
  return {
    type: AuthorizationSearchActionType.ChangeAuthoOwnershipFailure,
    error: error
  };
}



// WEBPACK FOOTER //
// ./src/authorization/_components/authorization-search/authorization-search.actions.tsx